import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  MdNotifications,
  MdCheckCircle,
  MdInfo,
  MdWarning,
  MdError,
} from "react-icons/md";
import { FiX } from "react-icons/fi";
import {
  getAllNotifications,
  getUnreadCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  deleteAllNotifications,
} from "../../redux/actions/notification";
import socketIO from "socket.io-client";
import { socket_url } from "../../server";
import { useNavigate } from "react-router-dom";

const AdminNotifications = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const notificationsState = useSelector(
    (state) => state.notifications
  );
  const notifications = notificationsState?.notifications || [];
  const unreadCount = notificationsState?.unreadCount || 0;
  const isLoading = notificationsState?.isLoading || false;
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Fetch notifications and unread count on component mount
  useEffect(() => {
    dispatch(getAllNotifications());
    dispatch(getUnreadCount());
  }, [dispatch]);

  // Periodically refresh unread count
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(getUnreadCount());
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [dispatch]);

  const isOpenRef = useRef(isOpen);

  // Keep track of latest isOpen value for socket listeners
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Socket IO for real-time notifications
  useEffect(() => {
    const newSocket = socketIO(socket_url, {
      withCredentials: true,
      transports: ["websocket", "polling"],
    });

    newSocket.emit("joinAdminRoom");

    // Fires when any admin notification is created (vendor approval, withdrawal, etc.)
    newSocket.on("newAdminNotification", (notification) => {
      console.log("[Admin Notification Received]", notification?._id);
      
      // Directly append to Redux state for optimistic updates
      dispatch({
        type: "createNotificationSuccess",
        payload: notification,
      });

      // Also trigger sidebar update event
      setTimeout(() => window.dispatchEvent(new Event('adminNotificationsUpdated')), 100);
    });

    // Leave room on cleanup
    return () => {
      newSocket.emit("leaveAdminRoom");
      newSocket.disconnect();
    };
  }, [dispatch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleMarkAsRead = (notificationId) => {
    dispatch(markNotificationAsRead(notificationId));
    setTimeout(() => window.dispatchEvent(new Event('adminNotificationsUpdated')), 500);
  };

  const handleMarkAllAsRead = () => {
    dispatch(markAllNotificationsAsRead());
    setTimeout(() => window.dispatchEvent(new Event('adminNotificationsUpdated')), 500);
  };

  const handleRemoveNotification = (notificationId) => {
    dispatch(deleteNotification(notificationId));
    setTimeout(() => window.dispatchEvent(new Event('adminNotificationsUpdated')), 500);
  };

  const handleClearAllNotifications = () => {
    dispatch(deleteAllNotifications());
    setTimeout(() => window.dispatchEvent(new Event('adminNotificationsUpdated')), 500);
  };

  const handleNotificationClick = (notification) => {
    // Make the notification completely disappear and decrement count
    handleRemoveNotification(notification._id);
    
    // navigate if needed (if actionUrl is implemented)
    if (notification.actionUrl) {
      navigate(notification.actionUrl, {
        state: { relatedId: notification.relatedId }
      });
    }
    setIsOpen(false);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "success":
        return <MdCheckCircle className="w-5 h-5 text-green-500" />;
      case "warning":
        return <MdWarning className="w-5 h-5 text-yellow-500" />;
      case "error":
        return <MdError className="w-5 h-5 text-red-500" />;
      default:
        return <MdInfo className="w-5 h-5 text-blue-500" />;
    }
  };

  const getCategoryBadge = (notification) => {
    const category = notification?.category || notification?.data?.category;
    const badges = {
      vendor_approval: { label: "Vendor Approval", color: "bg-purple-100 text-purple-700" },
      vendor_payment:  { label: "Vendor Payment",  color: "bg-green-100 text-green-700" },
      withdraw_request:{ label: "Withdrawal",       color: "bg-orange-100 text-orange-700" },
      withdraw_report: { label: "Report Chat",      color: "bg-blue-100 text-blue-700" },
    };
    const badge = badges[category];
    if (!badge) return null;
    return (
      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const notificationDate = new Date(timestamp);
    const diff = now - notificationDate;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <div className="relative">
      {/* Notification Button */}
      <button
        ref={buttonRef}
        onClick={toggleDropdown}
        className="relative p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
        title="Notifications"
      >
        <MdNotifications size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute right-0 top-full mt-2 w-96 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-96 overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Notifications
              </h3>
              <div className="flex items-center space-x-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Mark all read
                  </button>
                )}
                {notifications.length > 0 && (
                  <button
                    onClick={handleClearAllNotifications}
                    className="text-xs text-red-600 hover:text-red-700 font-medium"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
            {unreadCount > 0 && (
              <p className="text-sm text-gray-600 mt-1">
                You have {unreadCount} unread notification
                {unreadCount !== 1 ? "s" : ""}
              </p>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <MdNotifications className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No notifications yet</p>
                <p className="text-gray-400 text-xs mt-1">
                  You'll see important updates here
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification._id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer ${
                      !notification.isRead ? "bg-blue-50/50" : ""
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      {/* Notification Icon */}
                      <div className="flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification.type)}
                      </div>

                      {/* Notification Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                              {getCategoryBadge(notification)}
                            </div>
                            <p
                              className={`text-sm font-medium text-gray-900 ${
                                !notification.isRead ? "font-semibold" : ""
                              }`}
                            >
                              {notification.title}
                            </p>
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-400 mt-2 flex items-center space-x-2">
                              <span>
                                {formatTimestamp(notification.createdAt)}
                              </span>
                              {!notification.isRead && (
                                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                              )}
                            </p>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center space-x-1 ml-2">
                            {!notification.isRead && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkAsRead(notification._id);
                                }}
                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                title="Mark as read"
                              >
                                <MdCheckCircle size={16} />
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveNotification(notification._id);
                              }}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                              title="Remove notification"
                            >
                              <FiX size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
              <button 
                onClick={() => {
                  navigate("/admin-notifications");
                  setIsOpen(false);
                }}
                className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium text-center"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;
